import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setFilters, useTableFiltersDispatch, useTableFiltersState } from '../context/table_filters/TableFiltersIndex';
import { encodeFilters, decodeFilters } from '../utils/urlFilters';

/**
 * Custom hook to synchronize table filters with URL parameters
 * 
 * When filters are applied, pagination is automatically reset to page 1 (offset 0)
 * to prevent issues with empty result pages when the filtered data has fewer items
 * than the current pagination offset.
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.setOffset - Function to update offset state
 * @param {Function} options.setLimit - Function to update limit state
 * @param {Function} options.setFiltersCollapsed - Function to update filters collapsed state
 * @param {number} options.defaultLimit - Default value for limit (default: 15)
 * @param {Function} options.onUrlProcessed - Callback fired when URL parameters are processed
 * @returns {Object} - Contains properties and methods for URL filter handling
 */
const useUrlFilters = ({ 
  setOffset, 
  setLimit, 
  setFiltersCollapsed,
  defaultLimit = 15,
  onUrlProcessed = () => {}
}) => {
  const tableFilters = useTableFiltersState();
  const tableFiltersDispatch = useTableFiltersDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Ref to track initial load
  const initialLoadRef = useRef(false);
  
  // Update URL when filters change
  useEffect(() => {
    if (tableFilters) {
      const searchParams = new URLSearchParams(location.search);
      
      if (tableFilters.length > 0) {
        searchParams.set('filters', encodeFilters(tableFilters));
        // Reset offset to 0 when filters change to avoid empty pages
        searchParams.set('offset', '0');
        if (setOffset) {
          setOffset(0);
        }
      } else {
        searchParams.delete('filters');
      }
      
      navigate({
        pathname: location.pathname,
        search: searchParams.toString()
      }, { replace: true });
    }
  }, [tableFilters, navigate, location.pathname, setOffset]);
  
  // Initialize filters from URL on component mount
  useEffect(() => {
    // Only run once on component mount
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;
    
    const searchParams = new URLSearchParams(location.search);
    const filtersParam = searchParams.get('filters');
    const offsetParam = searchParams.get('offset');
    const limitParam = searchParams.get('limit');
    
    // Set pagination from URL if available
    if (offsetParam && setOffset) {
      setOffset(parseInt(offsetParam, 10));
    }
    
    if (limitParam && setLimit) {
      setLimit(parseInt(limitParam, 10));
    }
    
    // Set filters from URL if available
    if (filtersParam) {
      const decodedFilters = decodeFilters(filtersParam);
      if (decodedFilters.length > 0) {
        // When loading from URL, expand the filters section to show them
        if (setFiltersCollapsed) {
          setFiltersCollapsed(false);
        }
        setFilters(tableFiltersDispatch, decodedFilters);
      }
    }
    
    // Signal that URL parameters have been processed
    onUrlProcessed();
  }, [location.search, tableFiltersDispatch, setOffset, setLimit, setFiltersCollapsed, onUrlProcessed]);
  
  /**
   * Handle pagination click and update URL
   * 
   * @param {Event} e - Click event
   * @param {number} newOffset - New offset value
   * @param {number} newLimit - New limit value
   */
  const handlePaginationClick = (e, newOffset, newLimit) => {
    e.preventDefault();
    
    if (setOffset) setOffset(newOffset);
    if (setLimit) setLimit(newLimit);
    
    // Update URL with pagination params but keep filters
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('offset', newOffset);
    searchParams.set('limit', newLimit);
    
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  };
  
  // Return tableFiltersDispatch for testing purposes, not used in production code
  return {
    handlePaginationClick,
    tableFiltersDispatch
  };
};

export default useUrlFilters;